import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Building2, Landmark, ArrowUpRight } from "lucide-react";
import { PageHero, Section, Eyebrow } from "@/components/SharedUI";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  name: "",
  organisation: "",
  role: "",
  email: "",
  phone: "",
  projectStage: "",
  interest: "Platform",
  message: "",
};

const fieldDefs = [
  ["name", "Name", "text", true, "Aunty M."],
  ["organisation", "Organisation", "text", false, "Optional"],
  ["role", "Role", "text", false, "Optional"],
  ["email", "Email", "email", true, "you@community.org"],
  ["phone", "Phone", "tel", false, "Optional"],
  ["projectStage", "Project stage", "text", false, "e.g. scoping, planning, pilot"],
];

function buildContactPayload(form) {
  const subject = `${form.interest} enquiry — ${form.organisation || form.name}`;
  const messageLines = [
    form.role && `Role: ${form.role}`,
    form.phone && `Phone: ${form.phone}`,
    form.projectStage && `Project stage: ${form.projectStage}`,
    `Product interest: ${form.interest}`,
    "",
    form.message || "(no additional message)",
  ].filter(Boolean);
  return {
    name: form.name,
    email: form.email,
    organisation: form.organisation || null,
    subject,
    message: messageLines.join("\n"),
  };
}

function extractErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : "Could not send. Try again shortly.";
}

export default function Contact() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, buildContactPayload(form));
      if (res.data?.status === "received") {
        setSubmitted(true);
        trackEvent("contact_submitted", { product_interest: form.interest });
        toast.success("Discovery call request received. We'll be in touch.");
        setForm(INITIAL_FORM);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Request a confidential discovery call."
        desc="Use this form to discuss governance readiness, pilot pathways or enterprise licensing. Detailed product materials are shared under Background IP through direct engagement."
      />

      <section className="border-b border-stone-200">
        <Section>
          <div className="grid gap-10 lg:grid-cols-[1.1fr_.75fr]">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div className="grid gap-5 md:grid-cols-2">
                {fieldDefs.map(([key, label, type, required, placeholder]) => (
                  <div key={key}>
                    <Label htmlFor={key} className="text-[10px] tracking-[0.24em] uppercase font-bold text-stone-600">
                      {label} {required && <span className="text-[#9C4221]">*</span>}
                    </Label>
                    <Input
                      id={key}
                      type={type}
                      required={required}
                      value={form[key]}
                      onChange={update(key)}
                      data-testid={`contact-input-${key}`}
                      placeholder={placeholder}
                      className="mt-2 rounded-sm border-stone-300 focus-visible:ring-stone-900 focus-visible:ring-1"
                    />
                  </div>
                ))}
              </div>

              <div>
                <Label htmlFor="interest" className="text-[10px] tracking-[0.24em] uppercase font-bold text-stone-600">
                  Product interest
                </Label>
                <select
                  id="interest"
                  value={form.interest}
                  onChange={update("interest")}
                  data-testid="contact-input-interest"
                  className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-stone-900"
                >
                  <option>Platform</option>
                  <option>Toolkit</option>
                  <option>Both</option>
                  <option>Enterprise licence</option>
                </select>
              </div>

              <div>
                <Label htmlFor="message" className="text-[10px] tracking-[0.24em] uppercase font-bold text-stone-600">Message</Label>
                <Textarea
                  id="message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  className="mt-2 rounded-sm border-stone-300 focus-visible:ring-stone-900 focus-visible:ring-1 resize-none"
                  placeholder="Tell us about your community, project or governance need."
                />
              </div>

              <div className="flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-stone-500 max-w-sm">
                  By sending this message you understand BIGIBILA SYSTEMS™ is software, not legal advice.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Request Discovery Call"}
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>

              {submitted && (
                <div className="border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 text-sm" data-testid="contact-success">
                  Discovery call request received — a real person will respond.
                </div>
              )}
            </form>

            <aside className="space-y-6">
              <div className="border border-stone-200 bg-stone-50 p-6">
                <Eyebrow>Direct</Eyebrow>
                <div className="space-y-5 mt-2">
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500">Email</div>
                      <a href="mailto:info@bigibilasystems.com" className="text-sm text-stone-900 font-semibold hover:underline" data-testid="contact-email-link">
                        info@bigibilasystems.com
                      </a>
                      <div className="text-[11px] text-stone-500 italic">General enquiries, capability &amp; procurement.</div>
                      <div className="text-[11px] text-stone-500 mt-2 italic">
                        We respond within two business days.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500">Mobile</div>
                      <a href="tel:+61466943229" className="text-sm text-stone-900 font-semibold hover:underline" data-testid="contact-phone-link">
                        0466 943 229
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500">Entity</div>
                      <div className="text-sm text-stone-900">Bigibila Consultancy / Bigibila Systems</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Landmark className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500">Audience</div>
                      <div className="text-sm text-stone-900">Government, NFP and enterprise-ready governance products</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-6">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-700 mb-2">Before you write</div>
                <p className="text-sm text-stone-800 leading-relaxed">
                  If you're sharing anything sensitive, please don't send it through this form. Reach out first and we'll arrange an appropriate, custodian-respecting channel.
                </p>
              </div>
              <a
                href="/capability-statement"
                data-testid="contact-capability-statement"
                className="block border border-stone-300 bg-[#FAFAF9] p-6 hover:border-stone-900 transition-colors"
              >
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-2">
                  For procurement
                </div>
                <div className="font-display font-bold text-stone-900">Capability Statement →</div>
                <p className="mt-2 text-xs text-stone-600 leading-relaxed">
                  A4-printable one-pager covering entity details, services, methodology, compliance and engagement pathway.
                </p>
              </a>
            </aside>
          </div>
        </Section>
      </section>
    </>
  );
}
